import { PointSymbol, PointSymbolConstructorParams } from "@evergis/sgis/es/symbols/point/Point";
import { MultiPointSymbol } from "@evergis/sgis/es/symbols/MultiPointSymbol";
import { PointFeature } from "@evergis/sgis/es/features/PointFeature";
import { Crs } from "@evergis/sgis/es/Crs";
import { Render } from "@evergis/sgis/es/renders/Render";
import { Coordinates } from "@evergis/sgis/es/baseTypes";
import { Arc } from "@evergis/sgis/es/renders/Arc";

export type EditorPointSymbolParams = {
  shadowColor: string;
  shadowSize: number;
  outlineStrokeWidth?: number;
  outlineDash?: number[];
  outlineRadius?: number;
  mapCoordinates?: Coordinates;
} & PointSymbolConstructorParams;

export class ShadowedPointSymbol extends PointSymbol {
  shadowColor!: string;

  shadowSize!: number;

  constructor(options: EditorPointSymbolParams) {
    super(options);
  }

  static getShadowArc(
    position: Coordinates,
    { strokeWidth, shadowColor, shadowSize, size }: EditorPointSymbolParams,
    feature: PointFeature,
    mapCoordinates: Coordinates,
  ): Arc {
    return new Arc(position, {
      fillColor: "transparent",
      strokeColor: shadowColor,
      strokeWidth: shadowSize,
      radius: (size + strokeWidth + shadowSize) / 2,
      mapCoordinates,
    });
  }

  static getPointArc(
    position: Coordinates,
    { strokeWidth, fillColor, strokeColor, size }: EditorPointSymbolParams,
    feature: PointFeature,
    mapCoordinates: Coordinates,
  ): Arc {
    return new Arc(position, {
      fillColor,
      strokeColor,
      strokeWidth,
      radius: size / 2,
      mapCoordinates,
    });
  }

  static getOutlineArc(
    position: Coordinates,
    { fillColor, strokeColor, outlineRadius, outlineStrokeWidth, outlineDash }: EditorPointSymbolParams,
    feature: PointFeature,
    mapCoordinates: Coordinates,
  ): Arc {
    return outlineRadius
      ? new Arc(position, {
          fillColor,
          strokeColor,
          strokeWidth: outlineStrokeWidth,
          radius: outlineRadius,
          lineDash: outlineDash,
          mapCoordinates,
        })
      : null;
  }

  public renderFunction(feature: PointFeature, resolution: number, crs: Crs): Render[] {
    if (!(feature instanceof PointFeature)) return [];
    const { position } = feature.projectTo(crs);

    const pxPosition: Coordinates = [
      position[0] / resolution + (this.offset[0] || 0),
      -position[1] / resolution + (this.offset[1] || 0),
    ];

    const shadowRender = this.getShadow(pxPosition, feature, position);
    const pointArc = this.getPoint(pxPosition, feature, position);
    const outlineArc = this.getOutline(pxPosition, feature, position);

    let renders = shadowRender ? [shadowRender, pointArc] : [pointArc];

    if (outlineArc) {
      renders = [outlineArc, ...renders];
    }

    return renders;
  }

  protected getShadow(position: Coordinates, feature: PointFeature, mapCoordinates: Coordinates): Arc | void {
    return ShadowedPointSymbol.getShadowArc(position, this, feature, mapCoordinates);
  }

  protected getPoint(position: Coordinates, feature: PointFeature, mapCoordinates: Coordinates): Arc {
    return ShadowedPointSymbol.getPointArc(position, this, feature, mapCoordinates);
  }

  protected getOutline(position: Coordinates, feature: PointFeature, mapCoordinates: Coordinates): Arc | null {
    return ShadowedPointSymbol.getOutlineArc(position, this, feature, mapCoordinates);
  }
}

export const DEFAULT_EDITOR_POINT_OPTIONS = {
  size: 14,
  strokeWidth: 2,
  strokeColor: "#e33600",
  fillColor: "rgba(255, 255, 255, 0.46)",
  shadowColor: "rgba(227, 54, 0, 0.3)",
  shadowSize: 3,
};

export const editorPointSymbol = new ShadowedPointSymbol(DEFAULT_EDITOR_POINT_OPTIONS);

export const editorMultiPointSymbol = new MultiPointSymbol(editorPointSymbol);
